const OBSERVED_ATTRIBUTES = ['size-and-weight', 'ticket', 'primary-officer', 'issued-to-role-name', 'path-to-badge'];
class WeightSlip extends HTMLElement {
  //gets the attributes that are being observed

  static get observedAttributes() {
    return OBSERVED_ATTRIBUTES;
  }

  constructor() {
    super();
    //creates a shadow root for the component to attach to the DOM and render in isolation
    //mode open allows the shadow root to be accessed from outside the component
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  //observes the attributes and re-renders the component if the attribute changes
  attributeChangedCallback(name, oldValue, newValue) {
    if (OBSERVED_ATTRIBUTES.includes(name) && oldValue !== newValue) {
      this.render();
    }
  }

  //utility functions
  ordinalSuffixOf(i) {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "st";
    }
    if (j === 2 && k !== 12) {
      return i + "nd";
    }
    if (j === 3 && k !== 13) {
      return i + "rd";
    }
    return i + "th";
  }

  calculateAxleTotals(axleGroups = []) {
    if (!axleGroups || axleGroups?.length === 0) return { left: 0, right: 0, gross: 0 }

    const totals = axleGroups.reduce((acc, axleGroup) => {
      const { left, right } = axleGroup.axles.reduce((totals, axle) => {
        return {
          left: totals.left + (axle.leftAxleWeight || 0),
          right: totals.right + (axle.rightAxleWeight || 0),
        };
      }, { left: 0, right: 0 });

      return {
        left: acc.left + left,
        right: acc.right + right,
        gross: acc.gross + left + right,
      };
    }, { left: 0, right: 0, gross: 0 });

    return totals;
  }

  returnPlatformGrossWeight = (axleGroups = [], platformWeigh = true) => {
    if (!axleGroups || axleGroups?.length === 0) return 0;
    if (!platformWeigh) return 0;

    //reducer to iterate over the sizeAndWeight.axleGroups and sum the weight value on each
    try {
      const totalWeight = axleGroups.reduce((acc, axleGroup) => {
        return acc + +axleGroup.weight || 0;
      }, 0);
      return { gross: totalWeight, left: NaN, right: NaN };
    } catch (e) {
      console.error('Error calculating platform gross weight', e)
      return { gross: NaN, left: NaN, right: NaN };
    }
  }

  returnMostOverLimitAxleGroup(axleGroups = [], platformWeigh = true) {
    if (!axleGroups || axleGroups?.length === 0) return null;

    let maxOverLimit = 0;
    let mostOverLimitGroup = null;

    axleGroups.forEach(axleGroup => {
      if (axleGroup.axleGroupLimit) {
        let totalWeight = 0;
        if (platformWeigh) {
          totalWeight = axleGroup.weight || 0;
        } else {
          const { left, right } = axleGroup.axles.reduce((totals, axle) => {
            return {
              left: totals.left + (axle.leftAxleWeight || 0),
              right: totals.right + (axle.rightAxleWeight || 0),
            };
          }, { left: 0, right: 0 });
          totalWeight = left + right;
        }

        const overLimit = totalWeight - axleGroup.axleGroupLimit;

        if (overLimit > maxOverLimit) {
          maxOverLimit = overLimit;
          if (!platformWeigh) {
            mostOverLimitGroup = {
              ...axleGroup,
              axleSum: totalWeight
            }
          } else {
            mostOverLimitGroup = axleGroup;
          }
        }
      }
    });

    return mostOverLimitGroup;
  }

  convertUtcToLocal(utcString, includeTime = true, fourDigitYear = false) {
    // Check if utcString is already in ISO 8601 format
    if (!utcString) {
      return '';
    }
    const isIso8601 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|(\+|-)\d{2}:\d{2})$/.test(utcString);

    // If utcString is not in ISO 8601 format, convert it
    if (!isIso8601) {
      utcString = utcString?.replace(' ', 'T')?.replace(' UTC', 'Z');
    }

    if (isNaN(Date.parse(utcString))) {
      return '';
    }

    const date = new Date(utcString);
    const options = { year: fourDigitYear ? 'numeric' : '2-digit', month: '2-digit', day: '2-digit' };

    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = false;
    }

    return date.toLocaleString('en-US', options)?.replace(',', '')?.replace(':', '') || "";
  }

  formatNameFromPersonObj = (person) => {
    if (!person) {
      return ""
    } else if (!person?.personalAttribute && person?.name) {
      return person.name
    } else if (person?.personalAttribute) {
      if(person?.personalAttribute?.organization){
        return person.personalAttribute?.lastName
      }
      const firstName = person.personalAttribute?.firstName
      const lastName = person.personalAttribute?.lastName
      const formattedName = `${firstName ? firstName + " " : ""}${lastName ? lastName : ""}`
      return formattedName;
    } else {
      return ""
    }
  }
  //HTML generation
  generateHeaderHtml() {

    return `
    <div id="header">
      <img src="${this.getAttribute('path-to-badge')}" alt="Logo" id="logo" />
      <div id="address-title-container">
        <h3>
          State of Vermont<br/>
          Agency of Transportation<br />
          Department of Motor Vehicles<br />
          Commercial Vehicle Enforcement Unit<br />
          120 State Street, Montpelier, VT 05603<br />
        </h3>
          <h2 id="weight-slip-title">Weight Report-Trucks, Trailers and Semi Trailers</h2>
        </div>
      </div>`
  }

  generateDriverInfoHtml(sizeAndWeightObj = {}) {
    const { location, stateHighway, roadType, operator, operatorAddress } = sizeAndWeightObj || {}

    const { startedAt } = sizeAndWeightObj || ""
    return `
      <div id="driver-info" class="bold-bottom-border">
        <p id="location" class="large-width"><span class="bold">Location:</span> ${location?.name || ''}</p>
        <p id="route" class="small-width"><span class="bold">Route:</span> ${stateHighway?.name || ''}</p>
        <p id="start-date" class="medium-width"><span class="bold">Start Date:</span> ${this.convertUtcToLocal(startedAt) || ''}</p>
        <p id="road-type" class="small-width"><span class="bold">Road Type:</span> ${roadType?.name || ''}</p>
        <p id="operator-name" class="large-width"><span class="bold">Operator:</span> ${this.formatNameFromPersonObj(operator) || ''}</p>
        <p id="dob" class="medium-width"><span class="bold">DOB:</span> ${this.convertUtcToLocal(operator?.personalAttribute?.dob, false, true) || ''}</p>
        <p id="operator-address" class="huge-width"><span class="bold">Address:</span> ${operatorAddress?.name || ''}</p>
        <p id="operator-license" class="medium-width"><span class="bold">License:</span> ${operator?.personalAttribute?.driversLicense || ''}</p>
        <p id="operator-license-state"><span class="bold">State:</span> ${operator?.personalAttribute?.licenseState?.name || ''}</p>
      </div>`
  }

  generateCarrierInfoHtml(sizeAndWeightObj = {}) {
    const { powerUnit, } = sizeAndWeightObj || {}
    const { commodities } = sizeAndWeightObj || []
    const { permitNumberFiled, steerAxleTireSize, innerDimension, outerDimension, triaxleDimension } = sizeAndWeightObj || ""
    const { printOwner, printCarrier } = sizeAndWeightObj || false
    const person = printOwner ? sizeAndWeightObj?.owner : printCarrier ? sizeAndWeightObj?.carrier : ""
    const address = printOwner ? sizeAndWeightObj?.ownerAddress : printCarrier ? sizeAndWeightObj?.carrierAddress : ""
    const phone = printOwner ? sizeAndWeightObj?.ownerPhone : printCarrier ? sizeAndWeightObj?.carrierPhone : ""
    const steerAxleLimit = steerAxleTireSize * 600 * 2 || 0
    const commoditiesString = commodities?.map(commodity => commodity?.name).join(', ') || ''
    return `
    <div id="secondary-info" class="bold-bottom-border">
      <p id="secondary-name" class="name-width"><span class="bold">${printOwner ? "Owner" : printCarrier ? "Carrier" : ""}:</span> ${this.formatNameFromPersonObj(person) || ''}</p>
      <p id="secondary-phone" class="medium-width"><span class="bold">Telephone:</span> ${phone || ''}</p>
      <p id="secondary-address" class="full-width"><span class="bold">Address:</span> ${address?.name || ''}</p>
      <p id="power-unit-plate" class="medium-width"><span class="bold">Reg. No:</span> ${powerUnit?.vehiclePlate?.name || ''}</p>
      <p id="power-unit-state" class="small-width"><span class="bold">State:</span> ${powerUnit?.vehicleState?.name || ''}</p>
      <p id="permit-number" class="medium-width"><span class="bold">Permit #:</span> ${permitNumberFiled || ''}</p>
      <br/>
      ${this.generateAxleCheckboxesHtml(sizeAndWeightObj)}
      <p id="commodities" class="large-width"><span class="bold">Commodity:</span> ${commoditiesString || ''}</p>
      <p id="steer-axle-tire-size" class="medium-width"><span class="bold">Steer Axle Tire Size:</span> ${steerAxleTireSize + ' in' || ''}</p>
      <p id="steer-axle-limit" class="medium-width"><span class="bold">Steer Axle Weight:</span> ${steerAxleLimit?.toLocaleString() || ''}</p>
    
      <p id="bridge-formula-text" class="medium-width"><span class="bold">Bridge Formula</span></p>
      <p id="inner-dimension" class="small-width"><span class="bold">Inner</span> ${innerDimension ? innerDimension + ' ft' : ''}</p>
      <p id="outer-dimension" class="small-width"><span class="bold">Outer</span> ${outerDimension ? outerDimension + ' ft' : ''}</p>
      <p id="triaxle-dimension" class="small-width"><span class="bold">Triax</span> ${triaxleDimension ? triaxleDimension + ' ft' : ''}</p>
    </div>`
  }

  generateAxleCheckboxesHtml(sizeAndWeightObj) {

    const hasTrailer = sizeAndWeightObj?.trailers?.length > 0;

    function getTotalAxles(sizeAndWeightObject) {
      let totalAxles = 0;
      //iterate over the sizeAndWeightObject.axleGroups and sum the number of axles in each group
      sizeAndWeightObject?.axleGroups?.forEach(axleGroup => {
        totalAxles += axleGroup?.axles?.length || 0;
      });

      return totalAxles;
    }

    const totalAxles = getTotalAxles(sizeAndWeightObj);
    return `      
      <div id="axle-type">
        <label class="small-width"><span class="bold">2 Axle </span><input id="two" disabled="true" type="checkbox" ${!hasTrailer && totalAxles === 2 ? 'checked' : ''}></label>
        <label class="small-width"><span class="bold">3 Axle </span><input id="three" disabled="true" type="checkbox" ${!hasTrailer && totalAxles === 3 ? 'checked' : ''}></label>
        <label class="small-width"><span class="bold">4 Axle </span><input id="four" disabled="true" type="checkbox" ${!hasTrailer && totalAxles === 4 ? 'checked' : ''}></label>
        <label class="small-width"><span class="bold"> TT </span><input id="TT" disabled="true" type="checkbox" ${hasTrailer ? 'checked' : ''}></label>
        <p id="total-axles" class="small-width"><span class="bold">No of Axles:</span> ${totalAxles || ''}</p>
      </div>`
  }

  generateWeightLimitHtml(sizeAndWeightObj, ticket) {

    const { platformWeigh, courtAction } = sizeAndWeightObj || false
    const { axleGroups } = sizeAndWeightObj || []
    const { registeredWeight, roadLimitWeight } = sizeAndWeightObj || ""
    const weightCalculation = platformWeigh ? this.returnPlatformGrossWeight(axleGroups, platformWeigh) : this.calculateAxleTotals(axleGroups);
    const grossWeight = weightCalculation.gross
    const registeredOverUnder = grossWeight - registeredWeight;
    const roadOverUnder = grossWeight - roadLimitWeight;
    const mostOverLimitAxleGroup = this.returnMostOverLimitAxleGroup(axleGroups, platformWeigh);
    const mostOverLimitAxleGroupOverBy = mostOverLimitAxleGroup?.axleSum - mostOverLimitAxleGroup?.axleGroupLimit
    
    const { ticketNumber } = ticket || ""
    const violationName =
      ((ticket?.ticketViolationPrototype?.title || "") + " " +
        (ticket?.ticketViolationPrototype?.statute || "") + " " +
        (ticket?.ticketViolationPrototype?.section || "") + " " +
        (ticket?.ticketViolationPrototype?.code || "")).trim();

    return `
    <div id="weight-limits" class="bold-right-border">
      <p class="right-border"></p>
      <p></p>

      <p id="allowable-gross-label" class="right-border bold">Gross</p>
      <p id="allowable-gross" class="center">${grossWeight?.toLocaleString() || ''}</p>

      <p id="allowable-label" class="right-border bold">Allowable</p>
      <p id="allowable-limit" class="center">${registeredWeight?.toLocaleString() || ''}</p>

      <p id="allowable-over-under-label" class="right-border bottom-border bold">Over/Under</p>
      <p id="allowable-over-under-value" class="bottom-border center medium-font bold dark">${registeredOverUnder?.toLocaleString()}</p>

      <p class="bottom-border"></p>
      <p class="bottom-border"></p>

      <p id="road-gross-label" class="right-border bold">Gross</p>
      <p id="road-gross"  class="center">${grossWeight?.toLocaleString() || ''}</p>

      <p id="road-label" class="right-border bold">Road</p>
      <p id="road-limit" class="center">${roadLimitWeight?.toLocaleString() || ''}</p>

      <p id="road-over-under-label" class="right-border bottom-border bold">Over/Under</p>
      <p id="road-over-under-value" class="bottom-border center medium-font bold dark">${roadOverUnder?.toLocaleString()}</p>

      <p class="bottom-border"></p>
      <p class="bottom-border"></p>

      <p id="axle-group-label" class="right-border bold">Axle</p>
      <p id="axle-group-weight" class="center">${mostOverLimitAxleGroup?.axleSum?.toLocaleString() || ""}</p>

      <p id="axle-limit-label" class="right-border bold">Limit</p>
      <p id="axle-limit-weight" class="center">${mostOverLimitAxleGroup?.axleGroupLimit?.toLocaleString() || ""}</p>

      <p id="axle-over-under-label" class="right-border bottom-border bold">Over/Under</p>
      <p id="axle-over-under-value" class="bottom-border center medium-font bold dark">${isNaN(mostOverLimitAxleGroupOverBy) ? "" : (mostOverLimitAxleGroupOverBy)?.toLocaleString()}</p>

      <p class="bottom-border"></p>
      <p class="bottom-border"></p>

      <p id="court-label" class="right-border bold">Court</p>
      <p id="court-action" class="center">${courtAction ? 'Yes' : 'No'}</p>

      <p id="owner-operator-label" class="right-border bold">Owner/Operator</p>
      <p id="issued-to" class="center">${this.getAttribute('issued-to-role-name') || ''}</p>

      <p id="vcvc-label" class="right-border bold">VCVC #</p>
      <p id="vcvc-number" class="center">${ticketNumber || ""}</p>

      <p id="violation-label" class="right-border bold">Violation</p>
      <p id="violation-name" class="center">${violationName}</p>
    </div>`
  }

  generateAxleGroupHtml(axleGroups, platformWeigh) {
    const weightCalculation = platformWeigh ? this.returnPlatformGrossWeight(axleGroups, platformWeigh) : this.calculateAxleTotals(axleGroups);
    const grossWeight = weightCalculation.gross
    const leftTotal = weightCalculation.left
    const rightTotal = weightCalculation.right
    const axleHtml = this.generateAxleHtml(axleGroups, platformWeigh)
    return `
    <div id="axle-weights">
      <p id="axle-header" class="right-border medium-font bold">Axle</p>
      <p id="left-header" class="medium-font center bold">Left</p>
      <p id="right-header" class="medium-font center bold">Right</p>
      <p id="total-header" class="medium-font center bold">Total</p>
      <p class="right-border"></p>
      <p></p>
      <p></p>
      <p></p>
      ${axleHtml}
      <p class="right-border"></p>
      <p></p>
      <p></p>
      <p></p>
      <p id="total-label" class="right-border large-font bold">TOTAL</p>
      <p id="left-total" class="large-font center">${isNaN(leftTotal) ? "" : leftTotal?.toLocaleString()}</p>
      <p id="right-total" class="large-font center">${isNaN(rightTotal) ? "" : rightTotal?.toLocaleString()}</p>
      <p id="gross" class="large-font center dark bold">${grossWeight?.toLocaleString()}</p>
    </div>`
  }

  generateAxleHtml(axleGroups, platformWeigh) {

    let axleCount = 0;

    function hideZero(weight) {
      return weight?.toLocaleString() === '0' ? '' : weight?.toLocaleString()
    }
    return axleGroups.map((axleGroup, axleGroupIndex) => {
      const leftSubtotal = axleGroup.axles.reduce((total, axle) => total + (+axle.leftAxleWeight || 0), 0);
      const rightSubtotal = axleGroup.axles.reduce((total, axle) => total + (+axle.rightAxleWeight || 0), 0);
      const axleGroupTotal = platformWeigh ? axleGroup.weight : leftSubtotal + rightSubtotal;
      const axleJoinsHtml = axleGroup.axles.map((axle, axleIndex) => {
        axleCount++;
        const axleNumber = axleCount === 1 ? "Front" : this.ordinalSuffixOf(axleCount);
        const axleTotal = +axle.leftAxleWeight + +axle.rightAxleWeight
        return `
          <p id="axle-name-${axleGroupIndex}-${axleIndex}" class="bold right-border">${axleNumber} Axle</p>
          <p id="axle-left-weight-${axleGroupIndex}-${axleIndex}" class="center">${hideZero(axle.leftAxleWeight)}</p>
          <p id="axle-right-weight-${axleGroupIndex}-${axleIndex}" class="center">${hideZero(axle.rightAxleWeight)}</p>
          <p id="axle-total-weight-${axleGroupIndex}-${axleIndex}" class="center">${hideZero(axleTotal)}</p>
        `;
      }).join('');

      return `
        ${axleJoinsHtml}
        <p id="subtotal-label-${axleGroupIndex}" class="bold right-border">Sub Total</p>
        <p id="axle-group-left-subtotal-${axleGroupIndex}" class="center">${hideZero(leftSubtotal)}</p>
        <p id="axle-group-right-subtotal-${axleGroupIndex}" class="center">${hideZero(rightSubtotal)}</p>
        <p id="axle-group-subtotal-${axleGroupIndex}" class="bold center dark">${hideZero(axleGroupTotal)}</p>
        <p class="right-border"></p>
        <p></p>
        <p></p>
        <p></p>
      `;
    }).join('');
  }

  generateFineCalculationHtml(ticket) {

    function getWeightInThousands(weightLimit) {
      if (weightLimit?.name) {

        const name = weightLimit?.name;
        const pattern = /to ([\d,]+)/;
        const match = name.match(pattern);

        if (match) {
          const weightInPounds = Number(match[1].replace(/,/g, ''));
          const weightInThousands = weightInPounds / 1000;
          return weightInThousands;
        } else {
          return 0;
        }
      } else {
        return ""
      }
    }

    function mapViolationOccurrence(violationOccurrence) {
      const name = violationOccurrence?.name;

      if (!name) {
        return 0;
      }

      const nameToValueMap = {
        'First': 0,
        'Second': 0.05,
        'Third': 0.10,
        'Fourth': 0.15
      };

      const nameKey = name.split(' ')[0]; // Get the first word of the name

      return nameToValueMap[nameKey] || 0;
    }

    function formatNumber(num) {
      return num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    }
    // $15.00 for each 1,000 lbs. or portion thereof overweight for the first 5,000 lbs. overweight;
    // $30.00 for each 1,000 lbs. or portion thereof overweight when the gross overweight is more than 5,000 lbs. and less than 10,001 lbs.;
    // $45.00 for each 1,000 lbs. or portion thereof overweight when the gross overweight is more than 10,000 lbs. and less than 15,001 lbs.;
    // $60.00 for each 1,000 lbs. or portion thereof overweight when the gross overweight is more than 15,000 lbs. and less than 20,001 lbs.;
    // $90.00 for each 1,000 lbs. or portion thereof overweight when the gross overweight is more than 20,000 lbs. and less than 25,001 lbs.; and
    // $150.00 for each 1,000 lbs. or portion thereof overweight when the gross overweight is more than 25,000 lbs.
    const poundsOver = getWeightInThousands(ticket?.weightLimit) || 0
    const double = ticket?.ticketViolationPrototype?.name?.includes("+99K")
    let costPerK;

    if (poundsOver === 0) {
      costPerK = 0;
    } else if (poundsOver <= 5) {
      costPerK = 15;
    } else if (poundsOver <= 10) {
      costPerK = 30;
    } else if (poundsOver <= 15) {
      costPerK = 45;
    } else if (poundsOver <= 20) {
      costPerK = 60;
    } else if (poundsOver <= 25) {
      costPerK = 90;
    } else {
      costPerK = 150;
    }
    if (double) {
      costPerK *= 2
    }
    const fifteenPercentCharge = Math.ceil((+poundsOver * costPerK) * 0.15) || 0
    const subTotal = (+poundsOver * costPerK) + fifteenPercentCharge
    const occurrencePercent = mapViolationOccurrence(ticket?.violationOccurrence) || 0
    const occurrenceCharge = Math.ceil(subTotal * occurrencePercent) || 0
    const surcharge = !ticket?.ticketViolationPrototype?.name ? 0 : 47.00
    const total = !ticket?.ticketViolationPrototype?.name ? 0 : subTotal + occurrenceCharge + surcharge

    return `
    <div id="fine-container" class="bold-bottom-border">
      <p id="fine-header" class="center bottom-border bold">FINE CALCULATION</p>
      <div id="fine-calculation-container">
        <p id="pounds-over" class="center large-width">${poundsOver}</p>
        <p id="multiply" class="center">X</p>
        <p id="cost-per-k" class="center large-width">$${formatNumber(costPerK)}</p>
        <p id="first-plus" class="center">+</p>
        <p id="plus-fifteen-percent" class="center large-width">$${formatNumber(fifteenPercentCharge)}</p>
        <p id="first-equals" class="center">=</p>
        <p id="fine-subtotal" class="center large-width">$${formatNumber(subTotal)}</p>
        <p id="second-plus" class="center">+</p>
        <p id="occurrence-charge" class="center large-width">$${formatNumber(occurrenceCharge)}</p>
        <p id="third-plus" class="center">+</p>
        <p id="fine-surcharge" class="center large-width">$${formatNumber(surcharge)}</p>
        <p id="second-equals" class="center">=</p>
        <p id="fine-total" class="center large-width dark">$${formatNumber(total)}</p>
        <p id="pounds-over-label" class="center large-width"># K lbs Over</p>
        <p id="multiply-label" class="center">X</p>
        <p id="cost-per-k-label" class="center large-width">$ per K</p>
        <p id="first-plus-label" class="center">+</p>
        <p id="plus-fifteen-percent-label" class="center large-width">15%</p>
        <p id="first-equals-label" class="center">=</p>
        <p id="fine-subtotal-label" class="center large-width">Sub Total</p>
        <p id="second-plus-label" class="center">+</p>
        <p id="occurrence-charge-label" class="center large-width">${occurrencePercent * 100}%</p>
        <p id="third-plus-label" class="center">+</p>
        <p id="fine-surcharge-label" class="center large-width">Surcharge</p>
        <p id="second-equals-label" class="center">=</p>
        <p id="fine-total-label" class="center large-width bold">TOTAL</p>
      </div>
    </div>
    `
  }

  generateScaleHtml(scales = []) {
    // Create the header row
    let scaleHtml = `
    <div id="scales-container" class="bold-right-border">
      <p id="scale-number-label" class="bold center bottom-border">Scale Serial #</p>
      <p id="scale-make-label" class="bold center bottom-border">Make</p>
      <p id="scale-model-label" class="bold center bottom-border">Model</p>
    `;

    // Iterate over the scales array
    if (scales?.length > 0) {
      scales?.forEach((scale, scaleIndex) => {
        scaleHtml += `
        <p id="scale-number-${scaleIndex}" class="center">${scale?.serialNumber || ""}</p>
        <p id="scale-make-${scaleIndex}" class="center">${scale?.scaleMake?.name || ""}</p>
        <p id="scale-model-${scaleIndex}" class="center">${scale?.scaleModel?.name || ""}</p>
        `;
      });
    }
    scaleHtml += `</div>`;

    return scaleHtml;
  }

  generateSignatureHtml(sizeAndWeightObj, primaryOfficer) {
    const primaryOfficerName = (primaryOfficer?.personalAttribute?.firstName + " " + primaryOfficer?.personalAttribute?.lastName) || "";
    const badge = primaryOfficer?.deptAttribute?.badge || "";
    const { endedAt } = sizeAndWeightObj || ""
    const incidentNumber = sizeAndWeightObj?.incident?.incidentNumber || ""

    return `
      <div id="signature-container">
        <div id="signature-block">
          <p id="signature" class="bold-bottom-border">${primaryOfficerName}</p>
          <p id="signature-label" class="center">Signature</p>
        </div>
        <div id="id-time-incident-container">
          <p id="id-label" class="align-bottom">ID #</p>
          <p id="badge-number" class="bold-bottom-border align-bottom">${badge}</p>
          <p id="end-date-label" class="align-bottom">End Date:</p>
          <p id="ended-at" class="bold-bottom-border align-bottom">${this.convertUtcToLocal(endedAt) || ''}</p>
          <p id="incident-number-label" class="align-bottom">Incident #</p>
          <p id="incident-number" class="bold-bottom-border align-bottom">${incidentNumber}</p>
        </div>
      </div>
    `
  }

  render() {
    //Passed values
    const sizeAndWeight = JSON.parse(this.getAttribute('size-and-weight')) || {};
    const primaryOfficer = JSON.parse(this.getAttribute('primary-officer')) || {};
    const ticket = JSON.parse(this.getAttribute('ticket')) || {};


    //Size and weight values
    const platformWeigh = sizeAndWeight?.platformWeigh || false;
    const axleGroups = sizeAndWeight?.axleGroups || [];
    const scales = sizeAndWeight?.scales || [];


    // Generated HTML
    const headerHtml = this.generateHeaderHtml();
    const axleGroupHtml = this.generateAxleGroupHtml(axleGroups, platformWeigh);
    const driverInfoHtml = this.generateDriverInfoHtml(sizeAndWeight);
    const carrierInfoHtml = this.generateCarrierInfoHtml(sizeAndWeight);
    const weightLimitHtml = this.generateWeightLimitHtml(sizeAndWeight, ticket);
    const fineCalculationHtml = this.generateFineCalculationHtml(ticket);
    const scaleHtml = this.generateScaleHtml(scales);
    const signatureHtml = this.generateSignatureHtml(sizeAndWeight, primaryOfficer);

    this.shadowRoot.innerHTML = `
      <style>
      @page {
        size: Letter portrait;
        margin: 0;
      }

      html {
        box-sizing: border-box;
      }

      *,
      *:before,
      *:after {
        box-sizing: inherit;
      }

      html,
      body {
        margin: 0;
      }
      
      * {
        margin: 0;
        font-family: 'Arial', sans-serif;
      }

      article {
        display: flex;
        flex-direction: column;
        width: 8.5in;
        height: 11in;
      }

      p {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-left: 2pt;
        font-size: 11pt;
        /* background-color: rgb(232, 232, 232); */
      }

      label {
        padding-left: 2pt;
      }

      #header {
        position: relative; /* This is needed to position the logo relative to the header */
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
      }

      #logo {
        position: absolute;
        top: 0in; /* Adjust as needed */
        left: 0in; /* Adjust as needed */
        width: 1.5in;
        height: 1.5in;
      }

      #address-title-container{
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      #weight-slip-title {
        margin: 5pt 0;
      }

      #form-border-container {
        border: 2pt solid black;
        box-sizing: border-box;
      }

      #driver-info {
        display: grid;
        grid-template-columns: repeat(9, 1fr);
        grid-template-rows: repeat(3, 1fr);
        gap: 5pt;
        overflow: hidden;
      }

      #secondary-info {
        display: grid;
        grid-template-columns: repeat(10, 1fr);
        grid-template-rows: repeat(6, 1fr);
        gap: 5pt;
        overflow: hidden;
      }

      #axle-type {
        grid-row: 4;
        grid-column: 1/-1;
        display: grid;
        grid-template-columns: repeat(10, 1fr);
      }

      #weights-and-axles-container {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        overflow: hidden;
      }

      #weight-limits {
        grid-column: span 2;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(17, 1fr);
        overflow: hidden;
      }

      #axle-weights {
        grid-column: span 4;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-auto-rows: 1fr; /* Make all rows have equal height */
        overflow: hidden;
      }

      #fine-calculation-container {
        display: grid;
        grid-template-columns: repeat(34, 1fr);
        grid-template-rows: repeat(2, 1fr);
        overflow: hidden;
      }

      #scale-signature-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        overflow: hidden;
      }

      #scales-container {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        overflow: hidden;
      }

      #signature-container {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        overflow: hidden;
      }

      #signature-block {
        grid-row: 1/-1;
        grid-column: span 2;
        display: grid;
        grid-template-rows: repeat(2, 1fr);
      }

      #signature {
        align-items: flex-end;
        display: flex;
        justify-content: center;
        font-size: 13pt;
        font-family: 'Brush Script MT', cursive;
      }

      #id-time-incident-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, 1fr);
        overflow: hidden;
        grid-column: span 3;
        margin-bottom: 10pt;
      }

      #statute-container {
        display: grid;
        grid-template-columns: repeat(2, 2in);
        grid-template-rows: repeat(4, 1fr);
      }

      #footer {
        margin-top: auto;
        text-align: center;
      }

      .right-border {
        border-right: 1pt solid black;
      }

      .bottom-border {
        border-bottom: 1pt solid black;
      }
      .bold-bottom-border {
        border-bottom: 2pt solid black;
      }

      .bold-right-border {
        border-right: 2pt solid black;
      }

      .center {
        text-align: center;
      }

      .medium-font {
        font-size: 12pt;
      }

      .large-font {
        font-size: 13pt;
      }

      .dark {
        background-color: rgb(185, 185, 185);
      }

      .full-width {
        grid-column: 1/-1;
      }

      .address-width {
        grid-column: span 7;
      }

      .name-width {
        grid-column: span 7;
      }

      .huge-width {
        grid-column: span 5;
      }

      .large-width {
        grid-column: span 4;
      }

      .medium-width {
        grid-column: span 3;
      }

      .small-width {
        grid-column: span 2;
      }

      .align-bottom {
        display: flex;
        align-items: flex-end;
        justify-content: center;
      }

      .bold {
        font-weight: bold;
      }
      </style>
      <html>
      <head></head>
        <body>
          <article>
           ${headerHtml}
            <div id="form-border-container">
              ${driverInfoHtml}
              ${carrierInfoHtml}
              <div id="weights-and-axles-container" class="bold-bottom-border">
                ${weightLimitHtml}
                ${axleGroupHtml}
              </div>
                ${fineCalculationHtml}
              <div id="scale-signature-container">
                ${scaleHtml}
                ${signatureHtml}
              </div>
              </div>
              <div id="statute-container">
                <p>$ per K</p>
                <p>T23VSA1391a(b)(1)</p>
                <p>15%</p>
                <p>T13VSA7282(a)(9)</p>
                <p>Enhanced</p>
                <p>T23VSA1391a(b)(2)</p>
                <p>Surcharge</p>
                <p>T13VSA7282(a)(8)</p>
              </div>
              <div id="footer">
                <p>Vermont DMV CVE Unit (802) 828-2078</p>
              </div>
          </article>
        </body>
      </html>
    `;
  }
}

customElements.define('weight-slip', WeightSlip);
